import { Alert, Box, CircularProgress, SxProps } from '@mui/material';
import { AiPriseDataSavedEvent, AiPriseErrorEvent, AiPriseSessionEvent } from 'aiprise-web-sdk';
import React from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { calizaApi, useGetCustomerKYXVerificationQuery } from '@/api/v1';
import { AiPriseFrame } from '@/components/organisms/AiPriseFrame';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { Header } from '@/components/organisms/Header';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { BeneficiaryType } from '@/types/customer';
import { VerificationStatus } from '@/types/customerKYXVerification';

const HEADER_CONTENT_SX: SxProps = { width: '100%' };

export const ResumeVerificationModal: React.FC = (): React.ReactElement | null => {
  const { customerId = '' } = useParams<{ readonly customerId: string }>();
  const navigate = useNavigate();
  const tr = useTranslator();
  const toast = useToast();
  const dispatch = useDispatch();
  const {
    data: customer,
    isLoading,
    isError,
  } = useGetCustomerKYXVerificationQuery(customerId, {
    skip: !customerId,
  });

  const handleClose = React.useCallback((): void => {
    navigate('..');
  }, [navigate]);

  const handleAiPriseError = React.useCallback(
    (event: AiPriseErrorEvent): void => {
      console.error('❌ AiPrise Event: error', event.detail);
      toast.show(tr('CUSTOMER_VERIFICATION_ERROR'), 'error');
    },
    [toast, tr],
  );

  const handleAiPriseSuccessful = React.useCallback(
    (event: AiPriseSessionEvent): void => {
      console.debug('✅ AiPrise Event: successful', event.detail);
      toast.show(tr('CUSTOMER_VERIFICATION_SUCCESS'), 'success');

      dispatch(
        calizaApi.util.invalidateTags([
          { type: 'CustomerKYXVerifications', id: customerId },
          { type: 'CustomerKYXVerifications', id: 'LIST' },
        ]),
      );
    },
    [toast, tr, dispatch, customerId],
  );

  const handleAiPriseContinue = React.useCallback(
    (event: AiPriseSessionEvent): void => {
      console.debug('➡️ AiPrise Event: continue', event.detail);
      handleClose();
    },
    [handleClose],
  );

  const handleAiPriseResumed = React.useCallback((event: AiPriseSessionEvent): void => {
    console.debug('🔄 AiPrise Event: resumed', event.detail);
  }, []);

  const handleAiPriseDataSaved = React.useCallback((event: AiPriseDataSavedEvent): void => {
    console.debug('💾 AiPrise Event: data-saved', event.detail);
  }, []);

  const handleAiPriseDataRestored = React.useCallback((event: AiPriseSessionEvent): void => {
    console.debug('📂 AiPrise Event: data-restored', event.detail);
  }, []);

  if (isLoading) {
    return (
      <ApplicationDialog fullScreen>
        <Header title="RESUME_VERIFICATION" onClose={handleClose} contentSx={HEADER_CONTENT_SX} />
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress />
        </Box>
      </ApplicationDialog>
    );
  }

  if (isError || !customer) {
    return (
      <ApplicationDialog fullScreen>
        <Header title="RESUME_VERIFICATION" onClose={handleClose} contentSx={HEADER_CONTENT_SX} />
        <Alert severity="error">{tr('ERROR_LOADING_CUSTOMER')}</Alert>
      </ApplicationDialog>
    );
  }

  if (customer.verificationStatus !== VerificationStatus.NOT_STARTED) {
    return <Navigate to={`/customers/${customerId}`} replace />;
  }

  return (
    <ApplicationDialog fullScreen>
      <Header title="RESUME_VERIFICATION" onClose={handleClose} contentSx={HEADER_CONTENT_SX} />
      {customer.type === BeneficiaryType.business ? (
        <AiPriseFrame
          customerType={BeneficiaryType.business}
          sessionId={customer.sessionId}
          onError={handleAiPriseError}
          onSuccessful={handleAiPriseSuccessful}
          onContinue={handleAiPriseContinue}
          onResumed={handleAiPriseResumed}
          onDataSaved={handleAiPriseDataSaved}
          onDataRestored={handleAiPriseDataRestored}
          fullScreen
        />
      ) : customer.type === BeneficiaryType.individual ? (
        <AiPriseFrame
          customerType={BeneficiaryType.individual}
          sessionId={customer.sessionId}
          onError={handleAiPriseError}
          onSuccessful={handleAiPriseSuccessful}
          onContinue={handleAiPriseContinue}
          onResumed={handleAiPriseResumed}
          onDataSaved={handleAiPriseDataSaved}
          onDataRestored={handleAiPriseDataRestored}
          fullScreen
        />
      ) : null}
    </ApplicationDialog>
  );
};
